<template>
    <b-modal
        id="modal-create-event"
        ref="modal-create-event"
        centered
        ok-title="Aceptar"
        ok-variant="primary"
        cancel-title="Cancelar"
        :title="modalTitle"
        @ok="onCreateUpdate"
        @hidden="resetForm()"
    >
        <b-form id="event-form" @submit.stop.prevent="onSubmit()">
            <b-form-group
                id="input-group-event-name"
                label="*Nombre:"
                label-for="input-event-name"
            >
                <b-form-input
                    id="input-event-name"
                    v-model.trim="$v.form.name.$model"
                    name="name"
                    type="text"
                    required
                    :state="validateState('name')"
                    :disabled="formDisabled"
                />
                <b-form-invalid-feedback id="input-event-name-feedback">
                    <ul>
                        <li v-if="!$v.form.name.required">Este campo es requerido.</li>
                    </ul>
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
                id="input-group-event-description"
                label="*Descripción:"
                label-for="input-event-description"
            >
                <b-form-textarea
                    id="input-event-description"
                    v-model.trim="$v.form.description.$model"
                    name="description"
                    rows="3"
                    no-resize
                    :state="validateState('description')"
                    :disabled="formDisabled"
                />
                <b-form-invalid-feedback id="input-event-description-feedback">
                    <ul>
                        <li v-if="!$v.form.description.required">Este campo es requerido.</li>
                    </ul>
                </b-form-invalid-feedback>
            </b-form-group>

            <b-row>
                <b-col cols="12" md="6">
                    <b-form-group
                        id="input-group-event-start-date"
                        label="*Fecha Inicial:"
                        label-for="input-event-start-date"
                    >
                        <b-form-datepicker
                            id="input-event-start-date"
                            v-model="$v.form.startDate.$model"
                            placeholder="Seleccione una fecha..."
                            locale="es-ES"
                            :max="maxInputDates"
                            :state="validateState('startDate')"
                            :disabled="formDisabled || disableStartDate"
                            :date-format-options="dateInputFormat"
                            reset-button
                            hide-header
                            @input="setEndDate()"
                        />
                        <b-form-invalid-feedback id="input-event-start-date">
                            <ul>
                                <li v-if="!$v.form.startDate.required">Este campo es requerido.</li>
                            </ul>
                        </b-form-invalid-feedback>
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                    <b-form-group
                        id="input-group-event-end-date"
                        label="Fecha Final:"
                        label-for="input-event-end-date"
                    >
                        <b-form-datepicker
                            id="input-event-end-date"
                            v-model="$v.form.endDate.$model"
                            placeholder="Seleccione una fecha..."
                            locale="es-ES"
                            :min="minEndDate"
                            :max="maxInputDates"
                            :state="validateEndDateState()"
                            :disabled="formDisabled || !form.startDate"
                            :date-format-options="dateInputFormat"
                            reset-button
                            hide-header
                        />
                        <b-form-invalid-feedback id="input-event-end-date">
                            <ul>
                                <li v-if="!$v.form.endDate.required">Este campo es requerido.</li>
                            </ul>
                        </b-form-invalid-feedback>
                    </b-form-group>
                </b-col>
            </b-row>

        </b-form>
        <template v-slot:modal-footer="{ ok, cancel }">
            <!-- Button to delete display event delete confirmation -->
            <b-button
                v-if="selectedCrudObject"
                variant="danger"
                class="mr-1"
                @click="onDelete()"
            >
                <b-icon-trash-fill />
            </b-button>
            <!-- Emulate built in modal footer ok and cancel button actions -->
            <b-button
                variant="secondary"
                @click="cancel()"
            >
                Cancelar
            </b-button>
            <b-button
                variant="primary"
                @click="ok()"
                :disabled="disableOkButton"

            >
                Aceptar
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { mapState, mapGetters, mapActions } from 'vuex'
import { nowLocalDateObject } from '@/helpers.js'

export default {
    name: 'EventCreateUpdateDeleteModal',
    data () {
        return {
            formDisabled: false,
            modalTitle: 'Agregar Nuevo Evento',
            dateInputFormat: {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            },
            form: {
                eventId: null,
                name: '',
                description: '',
                startDate: '',
                endDate: ''
            },
            oldForm: {
                name: '',
                description: '',
                startDate: '',
                endDate: ''
            }
        }
    },
    validations: {
        form: {
            name: {
                required
            },
            description: {
                required
            },
            startDate: {
                required
            },
            endDate: {
                required
            }
        }
    },
    computed: {
        ...mapState([
            'activeSite',
            'selectedCrudObject',
            'selectedCalendarDate'
        ]),
        ...mapGetters([
            'getActiveSiteIdentifiers'
        ]),
        disableOkButton () {
            if (this.selectedCrudObject) {
                return this.formDisabled || this.$v.form.$invalid ||
                (this.form.name === this.oldForm.name &&
                this.form.description === this.oldForm.description &&
                this.form.startDate === this.oldForm.startDate &&
                this.form.endDate === this.oldForm.endDate)
            } else {
                return this.formDisabled || this.$v.form.$invalid
            }
        },
        minEndDate () {
            if (this.form.startDate) {
                return this.form.startDate
            }
            return ''
        },
        maxInputDates () {
            // The maximum allowed dates are from the current date until 2 years later
            var maxDate = nowLocalDateObject()
            maxDate.setFullYear(maxDate.getFullYear() + 2)
            return maxDate
        },
        disableStartDate () {
            if (this.selectedCalendarDate) {
                return true
            }
            return false
        }
    },
    watch: {
        selectedCrudObject (val, oldVal) {
            // If selectedCrudObject is given a value then the form enters Update mode
            // otherwise the form will always be in Create mode
            if (val) {
                this.modalTitle = 'Editar Evento'
                this.form.eventId = this.selectedCrudObject.eventId
                this.form.name = this.oldForm.name = this.selectedCrudObject.title
                this.form.description = this.oldForm.description = this.selectedCrudObject.description
                this.form.startDate = this.oldForm.startDate = this.getISODateFormat(this.selectedCrudObject.startDate)
                this.form.endDate = this.oldForm.endDate = this.getISODateFormat(this.selectedCrudObject.endDate)
            } else {
                this.resetForm()
            }
        },
        selectedCalendarDate (val, oldVal) {
            if (val) {
                this.form.startDate = val
                this.form.endDate = val
            }
        }
    },
    methods: {
        ...mapActions([
            'createSchedulingEvent',
            'updateSchedulingEvent',
            'deleteSchedulingEvent'
        ]),
        validateState (name) {
            const { $dirty, $error } = this.$v.form[name]
            return $dirty ? !$error : null
        },
        validateEndDateState () {
            const { $dirty, $error } = this.$v.form['endDate']
            let startDateState = this.validateState('startDate')

            if (startDateState) {
                if (!$dirty && !$error) {
                    return true
                }
            }
            return $dirty ? !$error : null
        },
        getISODateFormat (dateObj) {
            var day = dateObj.getDate().toString()
            var month = (dateObj.getMonth() + 1).toString()
            day = day.length < 2 ? `0${day}` : day
            month = month.length < 2 ? `0${month}` : month
            var dateString = `${dateObj.getFullYear()}-${month}-${day}`

            return dateString
        },
        async onCreateUpdate (bvModalEvt) {
            bvModalEvt.preventDefault() // Prevent modal from closing
            await this.onSubmit() // Trigger submit handler
        },
        setEndDate () {
            this.form.endDate = this.form.startDate
        },
        resetForm () {
            this.modalTitle = 'Agregar Nuevo Evento'
            this.form.eventId = null
            this.form.name = this.oldForm.name = ''
            this.form.description = this.oldForm.description = ''
            this.form.startDate = this.oldForm.startDate = ''
            this.form.endDate = this.oldForm.endDate = ''
            this.$v.$reset()
            this.$store.commit('RESET_SELECTED_CRUD_OBJECT')
            this.$store.commit('RESET_SELECTED_CALENDAR_DATE')
        },
        async onSubmit () {
            let loader = null
            try {
                this.formDisabled = true
                loader = this.$root.$loading.show()
                const payloadData = { ...this.form, courseSiteId: this.activeSite.id }
                var response
                if (this.selectedCrudObject) {
                    // Update mode
                    response = await this.updateSchedulingEvent(payloadData)
                    this.$store.commit('UPDATE_CALENDAR_EVENT', response)
                    this.$root.infoToast(`Evento editado.`)
                } else {
                    // Create mode
                    response = await this.createSchedulingEvent(payloadData)
                    this.$store.commit('CREATE_CALENDAR_EVENT', response)
                    this.$root.successToast(`Evento creado exitosamente.`)
                }
                this.$nextTick(() => {
                    this.$bvModal.hide('modal-create-event')
                })
                this.resetForm()
            } catch (err) {
                console.log({ err: err })
                if (err.response && (err.response.status === 404 || err.response.status === 400)) {
                    this.$root.dangerToast('Datos incorrectos, vuelva a intentar.')
                } else {
                    this.$root.unknownErrorToast()
                }
            } finally {
                this.formDisabled = false
                loader.hide()
            }
        },
        async onDelete () {
            try {
                let value = await this.$bvModal.msgBoxConfirm(
                    'Por favor confirme que desea eliminar el evento.', {
                        title: 'Confirmar',
                        okVariant: 'danger',
                        okTitle: 'Confirmar',
                        cancelTitle: 'Cancelar',
                        centered: true
                    }
                )
                if (value) {
                    await this.onConfirmDelete()
                }
            } catch (error) {
                console.log({ error: error })
                this.unknownErrorToast()
            }
        },
        async onConfirmDelete () {
            var loader = null
            try {
                loader = this.$loading.show()
                const payloadData = { courseSiteId: this.activeSite.id, eventId: this.selectedCrudObject.eventId }
                await this.deleteSchedulingEvent(payloadData)
                this.$store.commit('DELETE_CALENDAR_EVENT', this.selectedCrudObject.eventId)
                this.$nextTick(() => {
                    this.$bvModal.hide('modal-create-event')
                })
                this.$root.infoToast('El evento ha sido eliminado exitosamente.')
            } catch (error) {
                console.log({ error: error })
                this.$root.dangerToast('No se pudo eliminar el evento, intente más tarde.')
            } finally {
                loader.hide()
                this.$store.commit('RESET_SELECTED_CRUD_OBJECT')
            }
        }
    }
}
</script>
